import {
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  surveySubmissionsFiltersSchema,
  useSurvey,
  useSurveySubmissions,
} from 'store/surveys';
import { useRouteParams } from 'utils/useIdParam';
import { useSearchParams } from 'utils/navigation';
import moment from 'moment';
import { formatPercents } from 'utils/format';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  OrgStructureFilter,
} from 'components/filters';
import NumberRangeFilter from 'components/filters/NumberRangeFilter';
import { SubmissionsTrainingTabsSwitcher } from 'containers/surveys';
import { CompanyScopeInfoMessage } from 'containers/employees';

export const SurveySubmissionsTraining = () => {
  const { t, i18n } = useTranslation();
  const { id: surveyId } = useRouteParams(ROUTES.surveys.survey.details);

  const { searchParams: filters, navigate } = useSearchParams(
    surveySubmissionsFiltersSchema,
  );

  const { data: survey } = useSurvey(surveyId);
  const { data: submissions, isLoading: isSubmissionsLoading } =
    useSurveySubmissions(surveyId, filters);

  if (!survey) return null;

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <SubmissionsTrainingTabsSwitcher surveyId={surveyId} />

          <CompanyScopeInfoMessage />

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={submissions?.total} />

            <Filters
              value={filters}
              initialValues={surveySubmissionsFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              keysToExtractFromTotalCalculation={['search']}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('home.organisationStructure_title')}
                    value={value.orgStructure}
                    initialValue={initialValues.orgStructure}
                    onChange={(v) => setFilter('orgStructure', v)}
                    contentMaxWidth={420}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <OrgStructureFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('surveys.completedAt.label')}
                    value={value.completionDate}
                    initialValue={initialValues.completionDate}
                    onChange={(v) => setFilter('completionDate', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('surveys.score.label')}
                    value={value.score}
                    initialValue={initialValues.score}
                    onChange={(v) => setFilter('score', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <NumberRangeFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                  />
                </>
              )}
            </Filters>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={submissions?.items ?? []}
        isLoading={isSubmissionsLoading}
        columns={[
          {
            id: 'name',
            header: t('common.full_name'),
            cell: ({ row: { original: submission } }) =>
              submission.employee ? (
                <Link
                  label={submission.employee.name}
                  to={ROUTES.surveys[survey.type].submissions.details
                    .replace(':id', surveyId)
                    .replace(':submissionId', submission.id)}
                />
              ) : (
                <Typography color="textSecondary">
                  {t('trustBox.anonymousEmployeeName')}
                </Typography>
              ),
            meta: { sort: true },
          },
          {
            id: 'company',
            header: t('employees.company'),
            cell: ({ row: { original: submission } }) => {
              return submission.company?.name ?? '-';
            },
          },
          {
            id: 'department',
            header: t('employees.department'),
            cell: ({ row: { original: submission } }) => {
              return submission.department?.name ?? '-';
            },
            meta: { sort: true },
          },
          {
            id: 'startDate',
            header: t('surveys.startDate'),
            cell: ({ row: { original: submission } }) => {
              return submission.startDate
                ? moment(submission.startDate).format('l LT')
                : null;
            },
            meta: { sort: true },
          },
          {
            id: 'completionDate',
            header: t('surveys.completedAt.label'),
            cell: ({ row: { original: submission } }) => {
              return submission.completionDate
                ? moment(submission.completionDate).format('l LT')
                : null;
            },
            meta: { sort: true },
          },
          {
            id: 'submissionAttempt',
            header: t('surveys.attemt_number.label'),
            cell: ({ row: { original: submission } }) => {
              return submission.submissionAttempt;
            },
            meta: { align: 'right' },
          },
          {
            id: 'score',
            header: t('surveys.score.label'),
            cell: ({ row: { original: submission } }) => {
              return (
                <Typography
                  color={
                    submission.score >= survey.minScore ? 'success' : 'error'
                  }
                >
                  {formatPercents(i18n.language, submission.score / 100)}
                </Typography>
              );
            },
            meta: { sort: true, align: 'right' },
          },
        ]}
        pagination={
          submissions && {
            count: submissions.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
      />
    </TableWrapper>
  );
};
